import { Shimmer, ShimmerElementType } from "@fluentui/react";
import { Box, Grid } from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import DisplayAPIsException from "../../../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent, logException } from "../../../../helper/AppInsightsHelper";
import { ProductViewFiltersContext } from "../../../../pages/ProductView";
import { ChartOption } from "../../../../SharedComponents/Interfaces/CustomCardWithMetrics.interfaces";
import { useGetProductACRDetailsQuery } from "../../../../slices/ExploreProducts.slice";
import { CSS_STYLES } from "../../../CustomerView/ConsumptionRevenueTab/ConsumptionRevenueTab.styles";
import { useRevenueFilters } from "../ConsumptionRevenue/ConsumptionRevenueTab.hooks";
import ConsumptionRevenueTabTable from "../ConsumptionRevenue/ConsumptionRevenueTabTable";
import { RevenueFilterState, RevenueFilterableKey } from "./ConsumptionRevenueTab.interfaces";
import { ConsumptionRevenueTabCharts } from "./ConsumptionRevenueTabCharts";

const ConsumptionRevenueTab = () => {
  const filters: any = useContext(ProductViewFiltersContext);
  const productId = filters?.productId;

  // State
  const [currentFilter, setCurrentFilter] = useState<RevenueFilterState>({
    filterKey: "",
    value: ""
  });

  // API Call
  const shouldFetch = Number(productId) > 0;

  const { data, error, isLoading } = useGetProductACRDetailsQuery(
    productId,
    { skip: !shouldFetch }
  );

  // Memoized filtered items using custom hook
  const filteredItems = useRevenueFilters(data, currentFilter);

  // Chart data point click handler
  const handleChartDataPointClick = useCallback(
    (option: ChartOption) => {
      setCurrentFilter({
        filterKey: option.field as RevenueFilterableKey,
        value: option.value
      });
      logEvent("Consumption Revenue Tab - Chart Data Point Click", "Customer Page", "ConsumptionRevenueTab", {
        productId,
        chart: option.title,
        filterKey: option.field,
        filterValue: option.value
      });
    },
    [productId]
  );

  // Effect: Log event on mount
  useEffect(() => {
    logEvent("Consumption Revenue Tab", "Product View Page", "ConsumptionRevenueTab", {
      productId
    });
  }, [productId]);

  // Effect: Log exceptions on error
  useEffect(() => {
    if (error) {
      logException(
        "Product View Page - Consumption Revenue Tab",
        "ConsumptionRevenueTab",
        "useGetProductACRDetailsQuery",
        error,
        {
          productId: productId
        }
      );
    }
  }, [error, productId]);

  // Render error if present
  const renderError = () => error && !isLoading && <DisplayAPIsException error={error} height={250} />;

  return (
    <div>
      {isLoading ? (
        <Box sx={CSS_STYLES.shimmerBox}>
          <Grid container>
            <Grid item xs lg={6} xl={4} sx={{ pt: 2.5, pb: 2.5, pl: 1 }}>
              <Shimmer
                shimmerElements={[
                  { type: ShimmerElementType.line, height: 23, width: 100 },
                  { type: ShimmerElementType.gap, height: 23, width: 10 },
                  { type: ShimmerElementType.line, height: 23, width: 150 },
                  { type: ShimmerElementType.gap, height: 23, width: 10 },
                  { type: ShimmerElementType.line, height: 23, width: 150 },
                  { type: ShimmerElementType.gap, height: 23, width: 10 },
                  { type: ShimmerElementType.line, height: 23, width: 150 }
                ]}
                shimmerColors={{ shimmer: "var(--shimmer-color)" }}
                styles={CSS_STYLES.shimmerWrapper}
              />
            </Grid>
          </Grid>
        </Box>
      ) : (
        <>
          <Box sx={CSS_STYLES.chartBox}>
            <ConsumptionRevenueTabCharts
              loading={isLoading}
              onDataPointClick={handleChartDataPointClick}
              onSelectedChartFilter={setCurrentFilter}
              options={{
                chartOptions: [
                  {
                    field: "endOfMonth",
                    id: 1,
                    key: "overall-revenue-trend",
                    kustoLink: data?.kustoLink,
                    legendName: "Overall Revenue Trend",
                    title: "Overall Revenue Trend",
                    type: "line"
                  }
                ],
                filteredData: filteredItems,
                originalData: data?.revenueList || []
              }}
            />
          </Box>
          <Box sx={CSS_STYLES.tableBox}>
            {(!error && (
              <ConsumptionRevenueTabTable
                loading={isLoading}
                tableData={filteredItems}
                errorMessage={data?.errorMessage}
                kustoLink={data?.kustoLink}
              />
            )) ||
              ""}
            {renderError()}
          </Box>
        </>
      )}
    </div>
  );
};

export default ConsumptionRevenueTab;
